/**
 * Koredor Tour — script.
 *
 * Pure data. Each step has 4 narrative fields so a non-accountant grasps not
 * just WHAT a surface does but WHY it exists and HOW it ties to the rest of
 * Koredor. ~110 steps across 10 chapters. Manual advance (user controls pace).
 *
 * `route` (optional): when present, the tour auto-navigates to this path
 * before showing the step. `targetId` (optional): when present, the
 * spotlight highlights the element carrying `data-tour-id={targetId}`.
 *
 * Phase A additions:
 *  - `roles?`: which audience this step is most useful for. When the user
 *    picks a role filter, only steps tagged with that role (or untagged
 *    chapter openers) are shown.
 *  - `inExpress?`: included in the ~14-step Express view (the "exec deck"
 *    walkthrough). Defaults to false. Chapter openers + cross-module
 *    handoffs should be true.
 *  - `module?`: which Koredor module the step lives in. Used for
 *    chapter-level grouping in the overlay.
 */

export type TourRole = "leadership" | "finance" | "ops" | "field" | "admin";

export interface TourStep {
  id: string;
  title: string;
  /** Plain-English description of WHAT this surface/feature does. */
  what: string;
  /** WHY it matters — the business reason, not the technical reason. */
  why: string;
  /** HOW it connects to the rest of Koredor (named modules). */
  connects: string;
  /** What goes wrong WITHOUT this — the cost of skipping it. */
  without: string;
  route?: string;
  targetId?: string;
  roles?: TourRole[];
  inExpress?: boolean;
}

export interface TourChapter {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  module?: "overview" | "sales" | "accounting" | "field" | "network";
  /** Sidebar `data-tour-id` to fall back to when a step in this chapter has
   *  no `targetId`. Lets the ring land on the matching nav group, giving the
   *  user a stable "you are here" anchor between chapters. */
  navAnchor?: string;
  steps: TourStep[];
}


export const TOUR_CHAPTERS: TourChapter[] = [
  // ───────────────────────────────────────────────────────── 1
  {
    id: "orchestra",
    number: 1,
    title: "The Koredor Orchestra",
    subtitle: "One ecosystem. Five sections playing in time.",
    module: "overview",
    navAnchor: "nav-item-command-dashboard",

    steps: [
      {
        id: "orchestra-intro",
        title: "Why Koredor exists",
        what: "Most contractors run on five disconnected tools: a CRM for sales, a scheduling app for ops, a folder for the field, a spreadsheet for the warehouse, and QuickBooks for the books. Koredor replaces all five with one system where every event in one section automatically informs the others.",
        why: "Disconnection is the silent tax on contractor businesses. A sold deal that doesn't reach ops on time, a wrapped install that doesn't reach AR for two weeks, a bill coded to the wrong job — every gap costs money or margin. Koredor's value isn't any single feature; it's the handoffs between them.",
        connects: "The next 13 chapters walk one job end-to-end: Sales → Job → Field → Warehouse → Contractor Network → Accounting (Bills → WIP → AR → Flip → Reports → Year-End). Same job, every module, no copy-paste.",
        without: "You're licensing five products and paying a person to be the integration layer between them.",
        roles: ["leadership", "finance", "ops", "field", "admin"],
        inExpress: true,
      },
      {
        id: "orchestra-modules",
        title: "The five sections",
        what: "Sales (lead → sold appointment), Operations (job lifecycle, scheduling, dispatch), Field (installer execution, QA), Warehouse (materials, procurement, receiving), Network (contractors + payouts), and Books (the financial truth layer underneath all of it).",
        why: "Each section has its own daily-driver surface, but they share one canonical job model. There's no 'sync' between modules because there's nothing to sync.",
        connects: "Every section is reachable from the role-aware sidebar. Your role determines which section opens by default.",
        without: "You'd be reconciling the same job across five tools every Monday morning.",
        roles: ["leadership"],
        inExpress: true,
      },
      {
        id: "orchestra-rbac",
        title: "Role-aware everything",
        what: "Field crews never see contract values. Sales reps never see other reps' pipelines. Ops managers see scheduling but not commissions. The same job page renders differently depending on who's looking.",
        why: "Visibility is a security feature and a focus feature. Field crews making install decisions don't need P&L; CFOs reviewing margins don't need install photos.",
        connects: "Driven by `ROLE_ALLOWED_ROUTES` + `useFinancialPermissions`. Every page checks before rendering financial fields.",
        without: "Either you over-share (commissions leak to crews) or you under-share (CFO can't see what the field saw).",
        roles: ["leadership", "admin"],
      },
      {
        id: "orchestra-canonical",
        title: "One truth per domain",
        what: "There's exactly one resolver per question. 'What's this job worth?' → `getDisplayPrice()`. 'Who's the rep?' → `useCurrentRep()`. 'What's in inventory?' → `WarehouseInventoryContext`. UI components call resolvers; they never recompute.",
        why: "The bug you don't want is two parts of the app disagreeing about the same number. Canonical resolvers make that impossible by construction.",
        connects: "Every memory entry in `mem://architecture/canonical-*` is a load-bearing rule. Bridges enforce them at write-time.",
        without: "You'd ship the classic 'dashboard says $48k, invoice says $51k' bug that destroys customer trust.",
        roles: ["admin"],
      },
      {
        id: "orchestra-tour-map",
        title: "How this tour works",
        what: "14 chapters, ~55 steps. Full view for licensees who want the whole system, Express view (~14 steps) for execs, or filter by role to see only what's relevant to your seat.",
        why: "The point isn't to memorize every screen — it's to leave with a mental model of how the parts talk to each other.",
        connects: "Mode toggle in the top-right of this card. Resume any time from the Books rail.",
        without: "You'd click around in random order and miss the part that makes it all click — the handoffs.",
        roles: ["leadership", "finance", "ops", "field", "admin"],
        inExpress: true,
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 2
  {
    id: "lead-to-job",
    number: 2,
    title: "Lead → Sale → Job",
    subtitle: "Where every dollar Koredor tracks is born.",
    module: "sales",
    navAnchor: "nav-group-sales",

    steps: [
      {
        id: "sale-handoff",
        title: "Sold appointment → live Job",
        what: "When a sales rep marks an appointment Sold, Koredor automatically creates a Job with the contract value, customer, scope, and JobSpec already bound. No re-entry.",
        why: "The seam between sales and ops is where most contractors leak. The fewer human handoffs, the fewer dropped scopes and miscommunicated contracts.",
        connects: "`AppointmentResult` → `Job.financials.contractValue` (canonical). The JobSpec is the system-of-record for what was sold and what gets built.",
        without: "Ops re-keys the deal, makes a typo on a phase, and the field installs the wrong sku.",
        roles: ["leadership", "ops"],
        inExpress: true,
      },
      {
        id: "jobspec",
        title: "JobSpec — the contract made executable",
        what: "Every Job carries a versioned JobSpec: phases, photo requirements, checklists, BOM. Drift between what was sold and what was scheduled triggers a review gate.",
        why: "Most install disputes trace back to 'that's not what we agreed to.' A versioned spec makes the agreement a single document everyone reads from.",
        connects: "Bound at job creation. Read by Installer Execution, QA, and Project Review Gate.",
        without: "The crew installs from memory of a phone call.",
        roles: ["ops", "field"],
      },
      {
        id: "job-detail",
        title: "Job Detail — the project's home base",
        what: "Every signal about a job in one cockpit: financial summary, schedule, materials, permits, installers, change orders, timeline.",
        why: "PMs spend 30% of their day asking 'what's going on with X?' This page is the answer for any X.",
        connects: "Pulls from every operational and financial context. Read-only for field roles; full edit for ops/admin.",
        without: "PMs run their day from email threads and screenshots.",
        roles: ["ops", "leadership"],
        inExpress: true,
      },
      {
        id: "readiness-engine",
        title: "Readiness Engine — can this job actually run?",
        what: "A canonical 7-category score (financial, permitting, materials, scheduling, customer, build, network) telling you whether a job is ready to dispatch.",
        why: "Dispatching a job that isn't ready creates the most expensive kind of rework: a truck roll that has to be rescheduled.",
        connects: "Reads every operational signal. Blocks scheduling on critical-category failures.",
        without: "You'd schedule a job whose permit hadn't pulled and burn the install day.",
        roles: ["ops"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 3 (NEW — Field)
  {
    id: "field-execution",
    number: 3,
    title: "The Field Executes",
    subtitle: "What the crew sees on the truck.",
    module: "field",
    navAnchor: "nav-group-execution",

    steps: [
      {
        id: "installer-portal",
        title: "Installer Portal — the crew's daily view",
        what: "Each installer's home base: today's jobs, the route, the JobSpec for each stop, photo and checklist requirements, and a single button for check-in.",
        why: "Field crews don't want a CRM — they want a clear list of stops and what 'done' looks like at each one. Anything more is friction.",
        connects: "Reads jobs assigned via Dispatch. The JobSpec from Chapter 2 drives the checklist and photo requirements right here.",
        without: "Crews work from text messages and PDFs. Photos live on phones. Disputes 30 days later have no evidence.",
        route: "/installer-portal",
        roles: ["field", "ops"],
        inExpress: true,
      },
      {
        id: "execution-flow",
        title: "Execution Flow — phase by phase",
        what: "Each phase of the JobSpec is a gate: photos in, checklist complete, then advance. Strictness Mode (Velocity / Standard / Strict) controls how rigid the gates are.",
        why: "Phase gates are how you stop a 'done' job from being undone by a missing inspection photo or skipped step.",
        connects: "Phase completion feeds Readiness, QA, and ultimately the install-complete flip in the books.",
        without: "Jobs marked complete that aren't. Callbacks within 30 days you could have prevented with one more photo.",
        roles: ["field", "ops"],
      },
      {
        id: "qa-callbacks",
        title: "QA & Callbacks — the truth layer",
        what: "Append-only callback log per installer. Dedupes system reports from human reports separately so one event isn't double-counted.",
        why: "Callback rate is the most honest measure of installer quality. It can't be gamed because the source is the customer or the next crew.",
        connects: "Feeds the Execution Score (Chapter 5), which feeds the contractor's tier, which feeds Smart Dispatch.",
        without: "Quality is a feeling, not a number. The worst installers keep getting assigned because no one has the data to stop them.",
        route: "/execution/qa-callbacks",
        roles: ["ops", "leadership"],
      },
      {
        id: "install-today",
        title: "Install Today — the day-of cockpit",
        what: "Every job scheduled for today across every crew. Status, ETA, blockers, materials staged or not.",
        why: "Dispatch's last-mile view. When something goes wrong at 9am you find out at 9:05, not at 5pm wrap-up.",
        connects: "Reads from Scheduling, Warehouse staging, and crew check-ins.",
        without: "You'd discover the missing material when the crew calls from the customer's driveway.",
        route: "/execution",
        roles: ["ops"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 4 (NEW — Warehouse)
  {
    id: "warehouse-materials",
    number: 4,
    title: "Warehouse & Materials",
    subtitle: "Right material, right job, right day.",
    module: "field",
    navAnchor: "nav-item-warehouse",

    steps: [
      {
        id: "warehouse-home",
        title: "Warehouse — physical truth",
        what: "Live inventory by SKU and location. Receiving, allocation, staging, picking, and returns all live here.",
        why: "The bridge between the office's idea of what's in stock and what's actually on the shelf. Most stockouts are reconciliation failures, not procurement failures.",
        connects: "Canonical source for stock counts. Allocations bind material to specific jobs.",
        without: "Sales sells what isn't there. Dispatch schedules what can't be loaded.",
        route: "/warehouse",
        roles: ["ops"],
        inExpress: true,
      },
      {
        id: "procurement-pos",
        title: "Procurement — POs with vendor ETAs",
        what: "Purchase orders carry expected delivery dates per line. Vendor acknowledgements lock the ETA so it can't drift silently.",
        why: "Install scheduling is gated on material ETAs. A trustworthy ETA is the difference between a confident schedule and a hopeful one.",
        connects: "PO receipt posts inventory IN and creates a GRNI liability. The bill that follows clears GRNI and lands in AP.",
        without: "You'd schedule jobs against vendor optimism and reschedule a third of them.",
        route: "/procurement",
        roles: ["ops", "finance"],
      },
      {
        id: "receiving-3wm",
        title: "Receiving — three-way match",
        what: "Receive against the PO. The system matches PO line, receipt, and bill. Discrepancies flag for review before the bill posts.",
        why: "Three-way match is the standard control against vendor overbilling. Doing it at receipt is cheap; doing it at month-end is impossible.",
        connects: "Receiving event → Inventory up + GRNI liability. Vendor bill → GRNI clear + AP up.",
        without: "You'd pay for material you never received and discover it months later — if at all.",
        roles: ["ops", "finance"],
      },
      {
        id: "material-gating",
        title: "ESID — material-gated install dates",
        what: "The Earliest Safe Install Date engine refuses to schedule an install until critical materials are received (or within a tiered buffer based on vendor reliability).",
        why: "It encodes the rule every good ops manager already knows: don't roll a truck on a job that isn't materially ready.",
        connects: "Reads PO ETAs and vendor reliability. Override requires typed CONFIRM from ops_manager or admin.",
        without: "You'd book installs on hope and reschedule on disappointment.",
        roles: ["ops"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 5 (NEW — Network)
  {
    id: "contractor-network",
    number: 5,
    title: "The Contractor Network",
    subtitle: "How Koredor decides who installs what.",
    module: "network",
    navAnchor: "nav-group-execution",

    steps: [
      {
        id: "network-home",
        title: "Installer Network — the bench",
        what: "Every contractor on your bench with vetting status, reliability tier (Elite / Reliable / Watch / Risk), execution score, and active jobs.",
        why: "The network is an asset. Treating it like one — with explicit tiers and a kept score — is how you compound install quality over time.",
        connects: "Tier feeds Smart Dispatch. Vetting status gates assignment. Score feeds promotion and probation.",
        without: "You'd assign by gut. Good installers leave because work is uneven; bad installers stay because no one tracks the damage.",
        route: "/execution/installer-network",
        roles: ["ops", "leadership"],
        inExpress: true,
      },
      {
        id: "smart-dispatch",
        title: "Smart Dispatch — recommendation, not automation",
        what: "For any open job, Koredor surfaces the top 3 best-fit contractors with reasons (tier, distance, current load, skills match). The dispatcher always picks.",
        why: "AI dispatch sounds great until it sends your worst installer to your best customer. Recommendation + human gives speed without losing judgment.",
        connects: "Reads the canonical dispatch resolver. Manual override of a 'not recommended' contractor requires an explicit confirm.",
        without: "Dispatch becomes whoever the dispatcher remembers. Newer good installers never get a shot.",
        roles: ["ops"],
      },
      {
        id: "vetting-promotion",
        title: "Vetting & Promotion Queue",
        what: "Promotion to Approved status is a hard gate: license + insurance + ≥6 photos + skill checklist + ≥6 completed jobs + execution score ≥75 + manual approver with a written reason.",
        why: "Every contractor failure traces back to a vetting shortcut. Making the gate mechanical makes the standard real.",
        connects: "Approved status unlocks unrestricted assignment. Probation is derived from the score — never set manually.",
        without: "Standards drift. The next preventable callback is already on the schedule.",
        route: "/execution/promotion-queue",
        roles: ["ops", "admin"],
      },
      {
        id: "holdbacks-disputes",
        title: "Holdbacks & Disputes — money that protects quality",
        what: "Append-only holdback ledger per contractor and job. Disputes are a separate append-only event stream. Filing a dispute does not auto-pause the holdback.",
        why: "Holdbacks are how Koredor turns quality from a hope into a contract. Append-only means every release, pause, or reversal is provable.",
        connects: "Holdback release wires to AP. Dispute resolution feeds the contractor's record.",
        without: "Quality enforcement becomes 'we didn't pay them, I think.' That doesn't survive a chargeback.",
        roles: ["finance", "ops"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 6 (NEW — Governance / Control Tower)
  {
    id: "control-tower",
    number: 6,
    title: "Control Tower & Governance",
    subtitle: "Observation by default. Override by exception.",
    module: "overview",
    navAnchor: "nav-primary",

    steps: [
      {
        id: "control-tower-home",
        title: "Control Tower — the super-admin's view",
        what: "A read-only observation surface aggregating execution, friction, and governance signals across every contractor and job — without leaking contract values or PII.",
        why: "Operators need a god-view to find systemic problems early. But god-view ≠ god-mode: changes still flow through the same writers everyone else uses.",
        connects: "Reads canonical resolvers. Override Mode is a per-user 10-minute window with typed CONFIRM + reason + rate limit.",
        without: "You'd either fly blind or you'd give everyone write access and lose the audit trail.",
        route: "/admin/control-tower",
        roles: ["admin", "leadership"],
        inExpress: true,
      },
      {
        id: "observer-mode",
        title: "Pilot Views — see what a role sees",
        what: "From the avatar menu, super-admins can observe the app as a specific contractor or installer. Banner + lockdown prevent any accidental write.",
        why: "Most 'the app is broken' tickets are role-visibility issues. Pilot Views resolves them in seconds without giving you the user's password.",
        connects: "Tagged telemetry — observer activity is excluded from real metrics.",
        without: "You'd debug by screenshare. Or worse, by guessing.",
        roles: ["admin"],
      },
      {
        id: "friction-log",
        title: "Friction Log — the UX truth layer",
        what: "Append-only log of every moment users hit a wall: blocked actions, missing data, confusing states. Acknowledged separately so 'seen' is distinct from 'fixed.'",
        why: "Product quality is a function of friction reduction. You can only reduce what you measure.",
        connects: "Surfaces on Control Tower's Observation tab. Acknowledgements are the ONLY allowed write to observability sinks.",
        without: "You'd ship features and miss the rough edges that drive churn.",
        route: "/execution/friction",
        roles: ["admin", "leadership"],
      },
      {
        id: "enforcement-mode",
        title: "Soft vs. Hard Enforcement",
        what: "Network enforcement runs in Soft mode by default — same writers, same audit trail, but blocking rules become suggestions a human can override. Flipping to Hard requires a super-admin audit entry.",
        why: "Beta operations need to ship with their training wheels on. The toggle is explicit so nobody flips it by accident.",
        connects: "Suggestions have their own telemetry sink so accept / dismiss / override / ignored-expired rates are measurable.",
        without: "You'd either over-enforce and break good operators, or under-enforce and lose the standard.",
        roles: ["admin", "leadership"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 7 (was Ch 3 — Accounting)
  {
    id: "big-picture",
    number: 7,
    title: "The Big Picture — Books overview",
    subtitle: "Every job dollar has five lives.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "intro",
        title: "Welcome to the Books",
        what: "Accounting in a contracting business tracks one thing in five different states: a dollar that's been promised, owed, spent, earned, and finally banked. Each surface in this tour represents one transition between those states.",
        why: "Most contractors only track 'banked.' That's why margin shocks happen at year-end — by then it's too late to fix the job that lost money in March.",
        connects: "Sales (promised) → Pay Runs + AP (spent) → AR (earned) → Bank Rec (banked). Every other module feeds these books.",
        without: "You'd run the company on a checking-account balance. Cash-on-hand is the worst leading indicator a contractor can use.",
        route: "/accounting/close-readiness",
        targetId: "books-rail",
        roles: ["leadership", "finance"],
        inExpress: true,
      },
      {
        id: "close-readiness",
        title: "Close Readiness — the daily heartbeat",
        what: "A single 0-100 score telling you whether the books are clean enough to close the month. Drops the moment something breaks: an unreconciled bank, an aging WIP, an anomalous JE.",
        why: "Month-end close used to be a two-week scramble. With a continuous score, close is a verification — not a discovery.",
        connects: "Pulls signals from every accounting surface: Bank Rec, AR aging, AP aging, Anomaly Queue, period lag.",
        without: "You only find out the books are broken at month-end, after the damage is already in the P&L.",
        targetId: "close-readiness-score",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 4 (was Ch 2)
  {
    id: "bill-arrives",
    number: 8,
    title: "A Bill Arrives",
    subtitle: "From PDF in your inbox to a posted journal entry.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "doc-inbox",
        title: "Document Inbox — the front door",
        what: "Every PDF, photo, or vendor email lands here first. The system pre-reads vendor name, invoice number, and amount.",
        why: "AP entry is the #1 time-sink in a contractor's office. The closer we get to 'drop a PDF, click Convert,' the more hours the controller gets back.",
        connects: "Feeds AP. Documents stay attached to the resulting bill as audit evidence.",
        without: "Bookkeeping bottlenecks on data entry. Bills sit in someone's email for weeks. WIP and cash forecast go stale.",
        route: "/accounting/document-inbox",
        targetId: "doc-inbox-list",
        roles: ["finance"],
        inExpress: true,
      },
      {
        id: "convert-to-bill",
        title: "Convert to Bill — one click",
        what: "Click Convert and the bill dialog opens pre-filled with the detected vendor (fuzzy-matched against your vendor list), amount, and date.",
        why: "Eliminates the manual transcription step where most data-entry errors happen.",
        connects: "Vendor Memory recalls how this vendor's bills are usually coded, so the GL account pre-fills too.",
        without: "Every bill takes 3-5 minutes of typing. With 50 bills a week, that's a full day.",
        targetId: "doc-inbox-convert-btn",
        roles: ["finance"],
      },
      {
        id: "ap-bills",
        title: "Accounts Payable — what you owe, by vendor",
        what: "The full AP ledger. Every open bill, who it's owed to, what's coming due, what's overdue.",
        why: "This is the working surface for paying the right vendors at the right time without surprise cash crunches.",
        connects: "Bills tagged to jobs flow to WIP automatically. Bills tagged to expense accounts hit P&L the moment they're posted.",
        without: "You'd pay vendors out of memory and miss early-pay discounts, which compound to real money.",
        route: "/accounting/ap",
        targetId: "ap-bills-table",
        roles: ["finance"],
      },
      {
        id: "wip-routing",
        title: "Project → WIP auto-routing",
        what: "On a bill line, pick a project and the GL account auto-routes to 1200 Work-In-Process. Override per line if needed.",
        why: "WIP is the bucket where job costs live until the job completes. Matching costs to the right job is the entire foundation of job profitability.",
        connects: "WIP balance shows up on the Balance Sheet. When the job completes, WIP flips to COGS automatically (you'll see this in Chapter 7).",
        without: "Costs get coded straight to expense — which crushes margins on month-end P&L for any job that won't be invoiced until next month.",
        roles: ["finance", "ops"],
      },
      {
        id: "labor-1099",
        title: "1099 contractor labor — hours × rate on the bill line",
        what: "When a 1099 invoice lists hours, capture them right on the bill line. Cost type Labor. The system computes amount from hours × rate.",
        why: "Contractor labor is still labor. It needs to land in the labor bucket of WIP, not generic 'subcontractor expense.' This is what makes labor-cost variance accurate.",
        connects: "Pay Runs (internal labor) and 1099 bills both feed `getContractorLaborForJob()`. Job Profitability sees them as one labor cost.",
        without: "Internal vs. 1099 labor get reported in different buckets. You can't tell whether a job lost money because the helper was slow or because the sub overbid.",
        roles: ["finance"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 5 (was Ch 3)
  {
    id: "wip",
    number: 9,
    title: "Work in Progress",
    subtitle: "Where job costs live before the job completes.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "wip-aging",
        title: "WIP Aging — find the stale jobs",
        what: "Every job carrying a WIP balance, sorted by how long it's been sitting. Old WIP = costs that should already have flipped to COGS.",
        why: "Stale WIP usually means one of three things: the job is done but no one closed it, the job is stuck on a real problem, or someone miscoded a bill. All three are worth knowing.",
        connects: "Reads from GL. When you click into an old job, drill down to the bills and pay-run entries that built up the balance.",
        without: "WIP grows quietly, inflates Total Assets on the balance sheet, and hides the true gross margin until year-end.",
        route: "/accounting/reports/wip-aging",
        targetId: "wip-aging-table",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
      {
        id: "pay-runs-feed",
        title: "Pay Runs feed WIP too",
        what: "Internal crew payroll runs post to WIP the same way 1099 bills do — by job, with labor cost type.",
        why: "It doesn't matter who did the work; the job carries the cost. This is what makes labor cost reporting honest.",
        connects: "Pay Runs → AutoPostingBridge → WIP. The bridge is idempotent so re-posting can't double-count.",
        without: "Internal labor lives on a payroll report and never touches job cost. You'd think jobs were 30 points more profitable than they actually are.",
        roles: ["finance", "ops"],
      },
      {
        id: "wip-split",
        title: "Material vs. Labor — split inside WIP",
        what: "Every WIP balance is tracked by cost type. On the flip to COGS, materials go to 5100 COGS-Material and labor goes to 5200 COGS-Labor.",
        why: "Two contractors with the same gross margin can be very different businesses. The split tells you whether margin compression is a vendor problem or a labor problem.",
        connects: "P&L by trade. Job Profitability per-job split. Budget vs. Actuals on labor specifically.",
        without: "You'd only know margin moved — never why.",
        roles: ["finance", "leadership"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 6 (was Ch 4)
  {
    id: "customer-pays",
    number: 10,
    title: "The Customer Pays You",
    subtitle: "But that's not Revenue yet.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "ar-hub",
        title: "AR Collections — who owes you, how long",
        what: "Every open invoice, aging buckets, overpayments. The collections work-surface.",
        why: "Cash sitting in AR is cash you've earned but can't spend. AR aging is a leading indicator of cash crunches.",
        connects: "Invoices created from Sales → AR. Payments received → AR + Bank Rec. Customer Statements pull from here.",
        without: "Collections becomes 'who's complained loudest this week.' You leave money on the table from quiet non-payers.",
        route: "/accounting/ar",
        targetId: "ar-hub-table",
        roles: ["finance"],
        inExpress: true,
      },
      {
        id: "deferred-revenue",
        title: "Deferred Revenue — money received, not yet earned",
        what: "When a customer pays a deposit before the work is done, that's a liability — you owe them work. It sits in 2200 Deferred Revenue until the job completes.",
        why: "GAAP says you can't recognize revenue you haven't delivered. Recognizing too early inflates this month's P&L and creates a margin cliff later.",
        connects: "AR invoice posted → DR Accounts Receivable, CR Deferred Revenue. Cash posted → DR Cash, CR AR. Both happen automatically.",
        without: "Revenue gets booked on cash receipt. Your P&L looks great in Q1 and terrible in Q3 when the work actually happens.",
        roles: ["finance"],
      },
      {
        id: "unapplied-credits",
        title: "Unapplied Credits — when a customer overpays",
        what: "If a customer pays $5,000 against a $4,800 invoice, the extra $200 sits as an unapplied credit on their account — not as miscellaneous income.",
        why: "Their next invoice should net against the credit. Otherwise you're double-collecting and the customer eventually notices.",
        connects: "Surfaces as a KPI banner on AR Collections. Auto-applies on the next invoice for that customer.",
        without: "Customers email accounting saying 'you owe me $200,' which is the worst possible first sentence to receive.",
        roles: ["finance"],
      },
      {
        id: "ready-to-bill",
        title: "Ready to Bill — wrapped jobs awaiting final invoice",
        what: "Every job the field has finished but the office hasn't fully invoiced. Aged column shows how long the cash has been sitting.",
        why: "The #1 cause of late cash is not late-paying customers — it's late invoicing. This surface kills that gap.",
        connects: "Reads `installStatus === 'Install Complete'` from the job lifecycle. One click opens the Job One-Pager.",
        without: "Wrapped jobs sit unbilled for weeks. The PM thinks the office invoiced; the office is waiting on the PM. Meanwhile, you're floating $200k of work.",
        route: "/accounting/ar/ready-to-bill",
        targetId: "ready-to-bill-table",
        roles: ["finance", "ops"],
        inExpress: true,
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 7 (was Ch 5)
  {
    id: "the-flip",
    number: 11,
    title: "The Flip",
    subtitle: "The moment a job becomes profit.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "flip-intro",
        title: "Install Complete = automatic flip",
        what: "The moment a job is marked Install Complete, two things happen with zero human action: Deferred Revenue → Revenue, and WIP → COGS (split material/labor).",
        why: "This is the single most error-prone moment in contractor accounting. Doing it manually means inconsistent timing, missed jobs, and revenue/cost mismatches. Automating it makes monthly margin honest.",
        connects: "`ruleJobCompleted` in `postingRules.ts` watches install status. The AutoPostingBridge guarantees it fires exactly once per job (idempotent).",
        without: "You'd close months with revenue recognized but no matching COGS — or vice versa. P&L becomes fiction.",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
      {
        id: "flip-jes",
        title: "What actually posts",
        what: "Two journal entries fire automatically: (1) DR Deferred Revenue / CR Revenue for the contract amount, (2) DR COGS-Material + DR COGS-Labor / CR WIP for the accumulated job cost.",
        why: "Both entries post in the same period, so revenue and cost match. This is the matching principle made automatic.",
        connects: "Both JEs carry the job ID, so they appear on the Job One-Pager and Job Profitability report immediately.",
        without: "Manual entry skews timing. Revenue lands in May, costs land in June, and June looks like a disaster.",
        roles: ["finance"],
      },
      {
        id: "job-one-pager",
        title: "Job One-Pager — the full project, one screen",
        what: "Contract, billed-to-date, unbilled, WIP (split material/labor including 1099), COGS, projected vs. realized margin — for one project.",
        why: "Every operations decision a contractor makes ('should we add scope?' 'should we discount?') needs this view. Most contractors don't have it.",
        connects: "Pure derivation over GL, AP, AR, and contractor-labor selectors. Reflects every posted JE within seconds.",
        without: "PMs negotiate change orders blind. Office gives credits without knowing margin impact. Decisions get made on gut.",
        route: "/accounting/reports/job-profitability",
        targetId: "job-profitability-table",
        roles: ["finance", "ops", "leadership"],
        inExpress: true,
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 8 (was Ch 6)
  {
    id: "close-the-books",
    number: 12,
    title: "Closing the Books",
    subtitle: "Verify, don't discover.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "bank-rec",
        title: "Bank Reconciliation — every dollar accounted for",
        what: "Match every bank transaction to a JE in the ledger. Split-match supported (one bank line to several JEs). Vendor Memory auto-suggests categorizations for repeat vendors.",
        why: "Reconciliation is the only check that proves the books match reality. If you can't reconcile, you don't know your cash position.",
        connects: "Pulls statement lines from CSV imports. Writes match links to the GL. Feeds Close Readiness.",
        without: "You think you have $80k cash. You actually have $62k. You commit to a deposit on materials and bounce a payment.",
        route: "/accounting/bank-rec",
        targetId: "bank-rec-accounts",
        roles: ["finance"],
      },
      {
        id: "review-queue",
        title: "Anomaly Review Queue — the second set of eyes",
        what: "Deterministic checks flag suspicious entries: amount outliers (z-score), duplicate suspects, round-number placeholders ($1,000.00, $5,000.00).",
        why: "Most accounting mistakes are typos or copy-paste duplicates. The queue catches them before they hit financial statements.",
        connects: "Runs on every JE write. Filtered by a $250 materiality floor so it's signal, not noise.",
        without: "You find the duplicate $12,400 bill three months later when a vendor calls about an overpayment.",
        route: "/accounting/gl/review-queue",
        targetId: "review-queue-list",
        roles: ["finance"],
      },
      {
        id: "trial-balance",
        title: "Trial Balance — does the ledger balance?",
        what: "Every account's debit and credit totals for the period. Total debits must equal total credits, or something is broken at the foundation.",
        why: "If TB is out of balance, no report downstream is trustworthy. This is the first check before any close.",
        connects: "Every posting rule and manual JE is required to balance. The system rejects an unbalanced JE at write-time.",
        without: "You'd close a month on a P&L that doesn't tie to the balance sheet, and an auditor would catch it.",
        route: "/accounting/gl/trial-balance",
        targetId: "trial-balance-table",
        roles: ["finance"],
      },
      {
        id: "period-close",
        title: "Period Close — lock the month",
        what: "When a period closes, no new entries can post to it without a typed override. Reversing entries auto-walk to the next open period.",
        why: "Closed periods stay closed. Otherwise late entries sneak in after financials are issued — your March P&L silently changes in May.",
        connects: "Every write path checks period status. Reversals find the next open period automatically.",
        without: "Financial statements become moving targets. Audit trail becomes meaningless.",
        route: "/accounting/periods",
        targetId: "periods-list",
        roles: ["finance"],
      },
      {
        id: "audit-log",
        title: "Audit Log — append-only, forever",
        what: "Every write to the ledger — who, when, what, why — recorded permanently. Cannot be edited or deleted.",
        why: "Audit defense and internal trust. When something looks wrong, you can prove what actually happened.",
        connects: "Captures every JE post, void, reversal, and approval action.",
        without: "Disputes become 'he said, she said.' Insurance and tax audits become expensive.",
        route: "/accounting/gl/audit-log",
        targetId: "audit-log-list",
        roles: ["finance", "admin"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 9 (was Ch 7)
  {
    id: "cfo-view",
    number: 13,
    title: "What the CFO Sees",
    subtitle: "Operational intelligence built on a clean ledger.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "income-statement",
        title: "Income Statement — earnings this period",
        what: "Revenue minus COGS minus operating expenses. The 'are we making money' report.",
        why: "Run weekly, not monthly. Weekly visibility catches margin slippage while you can still act.",
        connects: "Pure aggregation of posted JEs by account class. Updates the moment a flip JE lands.",
        without: "You'd budget for next year based on tax-return numbers from 14 months ago.",
        route: "/accounting/reports/income-statement",
        targetId: "income-statement-title",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
      {
        id: "balance-sheet",
        title: "Balance Sheet — what we own and owe",
        what: "Assets (cash, AR, WIP, fixed assets) on one side, liabilities (AP, deferred revenue) and equity on the other. Always balances.",
        why: "P&L tells you about flow; balance sheet tells you about position. You need both to know whether the business is healthy.",
        connects: "WIP shows here as an asset. Deferred Revenue shows here as a liability. Both are products of earlier steps in this tour.",
        without: "You could be profitable on paper and insolvent in cash.",
        route: "/accounting/reports/balance-sheet",
        targetId: "balance-sheet-title",
        roles: ["finance", "leadership"],
      },
      {
        id: "cash-forecast",
        title: "13-Week Cash Forecast",
        what: "Projected cash position week-by-week for the next quarter, based on AR aging, AP due dates, recurring JEs, and known job billings.",
        why: "Cash is the only number that can kill a profitable business. A 13-week view turns 'we might be tight in November' into a specific number on a specific day.",
        connects: "Reads from AR (inflows), AP + recurring (outflows), and Ready-to-Bill (pipeline inflows).",
        without: "You'd run cash like a checking account and learn about a crunch the week it happens.",
        route: "/accounting/reports/cash-forecast",
        targetId: "cash-forecast-grid",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
      {
        id: "budget-variance",
        title: "Budget vs. Actuals",
        what: "Plan versus reality, by account, by month. Drill into any variance to see which JEs caused it.",
        why: "The budget is only valuable if you check it. Variance against budget is the closest a controller gets to an early-warning system.",
        connects: "Budget lines stored in BudgetsContext. Actuals roll up from GL the same way the Income Statement does.",
        without: "Budget becomes a planning ritual no one revisits. Drift accumulates silently.",
        route: "/accounting/reports/budget-variance",
        targetId: "budget-variance-table",
        roles: ["finance", "leadership"],
      },
      {
        id: "fixed-assets",
        title: "Fixed Assets & Depreciation",
        what: "Trucks, equipment, computers. The register tracks cost, accumulated depreciation, useful life, and disposal. Monthly depreciation posts automatically.",
        why: "Fixed assets are real money tied up in things that lose value. Tracking depreciation right gets you the tax deduction and keeps the balance sheet honest.",
        connects: "Disposal generates JEs automatically: clear cost + accumulated depreciation, recognize gain/loss.",
        without: "You'd never depreciate (overstating assets and net income) or you'd forget to retire sold equipment (overstating again).",
        route: "/accounting/fixed-assets",
        targetId: "fixed-assets-register",
        roles: ["finance"],
      },
    ],
  },

  // ───────────────────────────────────────────────────────── 10 (was Ch 8)
  {
    id: "year-end",
    number: 14,
    title: "Year-End & Audit",
    subtitle: "Where most contractors lose two weeks. We lose an hour.",
    module: "accounting",
    navAnchor: "books-rail",
    steps: [
      {
        id: "1099",
        title: "1099 Tracking — built up all year",
        what: "Every 1099-eligible vendor's year-to-date payments, tracked automatically as bills are paid. January 1099 filing is a download, not a project.",
        why: "1099 compliance is annual but the data accumulates daily. Doing it right requires having flagged vendors correctly all year.",
        connects: "Vendor records carry `is1099` flag. Every payment to a flagged vendor accumulates here.",
        without: "January becomes a frantic vendor email blast asking for W-9s and reconstructing payment totals from check registers.",
        route: "/accounting/1099",
        targetId: "vendor-1099-table",
        roles: ["finance"],
      },
      {
        id: "sales-tax",
        title: "Sales Tax Center",
        what: "Accrued sales tax by jurisdiction, with remittance workflow. Tracks what you owe to whom and when.",
        why: "Sales tax is collected from customers and held in trust until remitted. Mismanaging it is one of the few accounting mistakes that can result in personal liability.",
        connects: "Invoice posts → CR Sales Tax Payable. Remittance posts → DR Sales Tax Payable / CR Cash.",
        without: "You'd commingle sales tax with operating cash and discover the gap at the wrong moment.",
        route: "/accounting/sales-tax",
        targetId: "sales-tax-summary",
        roles: ["finance"],
      },
      {
        id: "recurring-jes",
        title: "Recurring Journal Entries",
        what: "Monthly entries that should always post on schedule: rent accrual, depreciation, prepaid amortization. Templated and auto-run.",
        why: "If a controller has to remember to post the same JE every month, it eventually gets missed.",
        connects: "Idempotent runner — won't double-post if the period already has the entry.",
        without: "Skipped recurring entries skew monthly P&L unpredictably.",
        route: "/accounting/gl/recurring",
        targetId: "recurring-list",
        roles: ["finance"],
      },
      {
        id: "accountant-export",
        title: "Accountant Export — hand off the year",
        what: "One click bundles trial balance, GL detail, JE history, AR/AP aging, fixed assets, and supporting documents into an accountant-ready package.",
        why: "The handoff to your CPA used to be a week of email back-and-forth. Now it's an export and a Calendly link.",
        connects: "Pulls from every accounting context. Includes audit log so the CPA can see provenance of any entry they question.",
        without: "Tax prep drags into March. CPA bills you for chasing data instead of giving advice.",
        route: "/accounting/export",
        targetId: "accountant-export-title",
        roles: ["finance", "leadership"],
        inExpress: true,
      },
      {
        id: "recap",
        title: "What you just saw",
        what: "Ten chapters. One unbroken chain from a lead in the CRM to a signed tax return. Sales hands off cleanly to Ops. Ops dispatches a ready job. Field execution feeds the books. The books close themselves. The CFO sees reality, not last month's reality.",
        why: "This is what 'one ecosystem' actually means: not a chatbot bolted onto QuickBooks, but a ledger that thinks in jobs, dispatch that thinks in margins, and a field experience that thinks in checklists. One system. One source of truth.",
        connects: "Sales → Jobs → Field → Pay Runs → AP → WIP → AR → Bank → Reports → Tax.",
        without: "You'd be running the business on spreadsheets, on intuition, and on hope. Like most contractors do.",
        roles: ["leadership", "finance", "ops", "field", "admin"],
        inExpress: true,
      },
    ],
  },
];

export const TOTAL_STEPS = TOUR_CHAPTERS.reduce((s, c) => s + c.steps.length, 0);

export function getStepByIndex(globalIndex: number): {
  chapter: TourChapter; step: TourStep; chapterIndex: number; stepIndexInChapter: number;
} | null {
  let cursor = 0;
  for (let ci = 0; ci < TOUR_CHAPTERS.length; ci++) {
    const ch = TOUR_CHAPTERS[ci];
    if (globalIndex < cursor + ch.steps.length) {
      const local = globalIndex - cursor;
      return { chapter: ch, step: ch.steps[local], chapterIndex: ci, stepIndexInChapter: local };
    }
    cursor += ch.steps.length;
  }
  return null;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Viewing modes (Phase A)
 * Returns the ordered list of GLOBAL step indices that should be played for
 * a given mode. The TourContext walks these instead of [0..TOTAL_STEPS).
 * ────────────────────────────────────────────────────────────────────────── */

export type TourViewMode =
  | { kind: "full" }
  | { kind: "express" }
  | { kind: "role"; role: TourRole };

export function getPlayableIndices(mode: TourViewMode): number[] {
  const all: number[] = [];
  let cursor = 0;
  for (const ch of TOUR_CHAPTERS) {
    ch.steps.forEach((s, i) => {
      const globalIdx = cursor + i;
      if (mode.kind === "full") {
        all.push(globalIdx);
      } else if (mode.kind === "express") {
        if (s.inExpress) all.push(globalIdx);
      } else if (mode.kind === "role") {
        // Untagged steps (admin/architecture) are hidden when filtering by
        // role; chapter openers (first step of each chapter) are always
        // included so the user still gets the chapter context.
        if (i === 0) all.push(globalIdx);
        else if (s.roles?.includes(mode.role)) all.push(globalIdx);
      }
    });
    cursor += ch.steps.length;
  }
  return all;
}
